import React from 'react';
import { Phone, Mail, MapPin, Navigation } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo } from '../data/mock';

const ContactSection = () => {
  const contactMethods = [
    {
      icon: Phone,
      title: 'Par téléphone',
      description: 'Appelez-nous directement pour un conseil ou un devis',
      value: companyInfo.phone,
      href: `tel:${companyInfo.phone.replace(/\./g, '')}`,
      cta: 'Appeler maintenant',
      primary: true
    },
    {
      icon: Mail,
      title: 'Par email',
      description: 'Envoyez-nous votre demande par email',
      value: companyInfo.email,
      href: `mailto:${companyInfo.email}`,
      cta: 'Envoyer un email',
      primary: false
    },
    {
      icon: MapPin,
      title: 'En boutique',
      description: 'Venez nous rencontrer directement !',
      value: companyInfo.address,
      href: companyInfo.googleMapsUrl,
      cta: 'Voir l\'itinéraire',
      primary: false,
      external: true
    }
  ];

  return (
    <section id="contact" className="py-24 bg-white dark:bg-gray-900 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary-cyan/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-blue/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-2 bg-primary-cyan/10 text-primary-blue dark:text-primary-cyan font-semibold rounded-full text-sm mb-4">
            Contact
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Contactez-nous
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-blue to-primary-cyan"> facilement</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            Pour un devis, une question ou un conseil, contactez-nous par téléphone, email ou passez directement en boutique !
          </p>
        </div>

        {/* Contact Methods */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {contactMethods.map((method, index) => (
            <a
              key={index}
              href={method.href}
              target={method.external ? '_blank' : undefined}
              rel={method.external ? 'noopener noreferrer' : undefined}
              className={`group p-8 rounded-3xl border-2 transition-all duration-300 hover:-translate-y-2 ${
                method.primary 
                  ? 'bg-gradient-to-br from-primary-blue to-primary-blue/90 border-primary-blue text-white shadow-xl hover:shadow-2xl' 
                  : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 hover:border-primary-cyan hover:shadow-xl'
              }`}
            >
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110 ${
                method.primary ? 'bg-white/20' : 'bg-primary-cyan/10'
              }`}>
                <method.icon className={`w-8 h-8 ${method.primary ? 'text-white' : 'text-primary-blue dark:text-primary-cyan'}`} />
              </div>
              
              <h3 className={`text-xl font-bold mb-2 ${method.primary ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                {method.title}
              </h3>
              
              <p className={`text-sm mb-4 ${method.primary ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                {method.description}
              </p>
              
              <p className={`text-lg font-semibold mb-6 ${method.primary ? 'text-white' : 'text-primary-blue dark:text-primary-cyan'}`}>
                {method.value}
              </p>
              
              <div className={`inline-flex items-center gap-2 font-semibold ${
                method.primary ? 'text-white' : 'text-primary-blue dark:text-primary-cyan'
              }`}>
                {method.cta}
                <span className="transition-transform group-hover:translate-x-1">→</span>
              </div>
            </a>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href={`tel:${companyInfo.phone.replace(/\./g, '')}`}>
            <Button className="bg-primary-blue hover:bg-primary-blue/90 text-white px-8 py-6 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-3">
              <Phone className="w-5 h-5" />
              Appeler le {companyInfo.phone}
            </Button>
          </a>
          <a href={companyInfo.googleMapsUrl} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="border-2 border-primary-blue dark:border-primary-cyan text-primary-blue dark:text-primary-cyan hover:bg-primary-blue hover:text-white px-8 py-6 rounded-full text-lg font-semibold transition-all duration-300 flex items-center gap-3">
              <Navigation className="w-5 h-5" />
              Venir en boutique
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
